<template>
  <aside class="intro">
    <div class="intro__glow" aria-hidden="true"></div>

    <div class="intro__body">
      <h2 class="intro__lead">
        The CurriculumFinder is a searchable database of academic catalogs.
      </h2>

      <p>
        <strong>Coverage</strong> is comprehensive and includes public and
        private institutions from every state. Deep archives are available up
        to 10 years in most cases. Only official catalogs are used and no
        third-party data or advertising is included. Users may sort and search
        by college, state, educational level and Carnegie classifications.
      </p>

      <p>
        The CurriculumFinder is today's essential resource for transcript
        evaluators, researchers and libraries. <strong>Pricing</strong> is
        transparent and can be less expensive than legacy systems.
      </p>

      <ul class="intro__features">
        <li v-for="feature in features" :key="feature.title">
          <span class="intro__check" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M4 12.5l5 5 11-12" /></svg>
          </span>
          <div>
            <div class="intro__feature-title">{{ feature.title }}</div>
            <div class="intro__feature-desc">{{ feature.desc }}</div>
          </div>
        </li>
      </ul>

      <p class="intro__licensing">
        Data licensing is also available. Contact
        <a href="mailto:licensing@degreedata.com">licensing@degreedata.com</a>.
      </p>
    </div>

    <img class="intro__logo" :src="logoSrc" alt="DegreeData" />
  </aside>
</template>

<script>
// What a logged-out visitor reads next to the signup form: Anne's description
// of CurriculumFinder (her copy, 1 Oct 2026; spelling tidied), then three of the
// features from src/auth/FeaturesTeaser.vue, edited to CurriculumFinder alone.
// CurriculumFinder-specific, so it lives here rather than in FeaturesTeaser,
// which is meant to ship unchanged to CoverageReport. (Its "one login for every
// DegreeData tool" point is left out until the shared session is confirmed.)
import logoSrc from "@/assets/degreedata-logo.png";

export default {
  name: "LandingIntro",
  data() {
    return {
      logoSrc,
      features: [
        {
          title: "Find courses any way you search",
          desc: "Search catalogs by course title, code, or keyword, and jump straight to the page.",
        },
        {
          title: "Web & PDF catalog coverage",
          desc: "Search PDF catalogs right here; web catalogs open straight to the source.",
        },
        {
          title: "Cancel anytime, no credit card to start",
          desc: "30 days of full access. Keep using it month-to-month, or walk away. No surprises.",
        },
      ],
    };
  },
};
</script>

<style scoped>
.intro {
  position: relative;
  overflow: hidden;
  padding: 56px 48px;
  background: var(--wash);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  color: var(--ink-soft);
  font-family: var(--font-sans);
  text-align: left;
}

.intro__glow {
  position: absolute;
  top: -120px;
  right: -120px;
  width: 420px;
  height: 420px;
  background: var(--grad);
  opacity: 0.1;
  filter: blur(80px);
  border-radius: 50%;
  pointer-events: none;
}

.intro__body {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 15px;
  line-height: 1.6;
}

.intro__body p {
  margin: 0;
}

.intro__body strong {
  color: var(--ink);
  font-weight: 600;
}

.intro__lead {
  margin: 0 0 8px;
  font-family: var(--font-serif);
  font-size: 26px;
  line-height: 1.3;
  font-weight: 400;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.intro__features {
  list-style: none;
  margin: 8px 0 0;
  padding: 20px 0 0;
  border-top: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.intro__features li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.intro__check {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: var(--ink);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.intro__check svg {
  width: 11px;
  height: 11px;
  fill: none;
  stroke: #fff;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.intro__feature-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.4;
}

.intro__feature-desc {
  font-size: 13.5px;
  color: var(--mute);
  margin-top: 1px;
  line-height: 1.45;
}

.intro__licensing {
  margin-top: 8px;
  font-size: 13.5px;
  color: var(--mute);
}

.intro__licensing a {
  color: var(--ink);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.intro__licensing a:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}

.intro__logo {
  position: relative;
  width: 112px;
  height: auto;
  align-self: flex-start;
}

@media (max-width: 900px) {
  .intro {
    padding: 32px 24px;
    gap: 28px;
  }
  .intro__lead {
    font-size: 22px;
  }
}
</style>
